import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { CrmInvoiceResponse, ReduxEntity } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { extractIds, normalizeItems } from 'Douleutaras/utilities/redux/normalizeItems';
import { Dictionary } from 'lodash';
import { RootState } from 'Douleutaras/redux/store';

const TRANSACTIONS_ENDPOINT = 'api/public/crm_invoices';
const SINGLE_INVOICE_ENDPOINT = 'api/public/invoice';

const transactionsAdapter = createEntityAdapter<CrmInvoiceResponse>();

interface GetCrmInvoicesInterface {
  page: string;
  payed: string;
}

export const getCrmInvoices = createAsyncThunk(
  'crm_invoices/get',
  async ({page, payed}: GetCrmInvoicesInterface , api) => {

    const response = await makeRequest(
      `${TRANSACTIONS_ENDPOINT}/`,
      {
        method: 'GET',
        additionalGetParams: {
          page,
          type: 'invoice',
          payed
        }
      }
    )(api);

    const normalizedResults = normalizeItems(response.data.results);

    return { ...response.data, normalizedResults };
  }
);

export const getInvoice = createAsyncThunk<Dictionary<CrmInvoiceResponse>, { id: number }>(
  'invoice/get',
  async ({ id }, api) => {
    const response = await makeRequest(`${SINGLE_INVOICE_ENDPOINT}/${id}/`)(api);

    return normalizeItems<CrmInvoiceResponse>([response.data]);
  }
);

export const invoicesCrmSlice = createSlice({
  name: 'crmInvoicesItems',
  initialState: transactionsAdapter.getInitialState({
    totalTransactions: 0,
    currentPageItems: [],
    entities: {},
    loading: false
  } as ReduxEntity<CrmInvoiceResponse> & { totalTransactions: number; currentPageItems: Array<number> }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getCrmInvoices.pending, state => {
      state.loading = true;
    });
    builder.addCase(getCrmInvoices.fulfilled, (state, action) => {
      state.loading = false;
      transactionsAdapter.upsertMany(state, action.payload.normalizedResults || {});
      state.totalTransactions = action.payload.count;
      // @ts-ignore
      state.currentPageItems = extractIds(action.payload.results);
    });
    builder.addCase(getInvoice.fulfilled, (state, action) => {
      transactionsAdapter.upsertMany(state, action.payload);
    });
  }
});

const adapterSelectors = transactionsAdapter.getSelectors(
  (state: RootState) => state.crmInvoices
);

// @ts-ignore
export const selectInvoiceById = createSelector(
  [adapterSelectors.selectEntities, (state: RootState, invoiceId: number) => invoiceId],
  (entities, invoiceId) => entities[invoiceId]
);
